import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { cn } from '@/utils/cn';
import { CalendarPopover } from '@/components/ui/CalendarPopover';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { PickerPopoverShell } from '@/components/ui/PickerPopoverShell';
import { usePickerPopover } from '@/components/ui/usePickerPopover';
import {
  displayDate,
  formatDateValue,
  parseDateValue,
} from '@/utils/dateInput';

export interface DatePickerProps {
  label?: string;
  error?: string;
  description?: string;
  hint?: string;
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

export function DatePicker({
  label,
  error,
  description: _description,
  hint: _hint,
  value = '',
  onChange,
  min,
  max,
  disabled,
  required,
  placeholder: placeholderProp,
  className,
  id,
  name,
}: DatePickerProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('picker.selectDate');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { panelRef, panelCoords } = usePickerPopover(open, triggerRef, {
    estimatedHeight: 340,
    minWidth: 288,
    maxWidth: 320,
  });
  const [viewMonth, setViewMonth] = useState(() => parseDateValue(value) ?? new Date());

  useEffect(() => {
    const parsed = parseDateValue(value);
    if (parsed) setViewMonth(parsed);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [panelRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (date: Date) => {
    onChange?.(formatDateValue(date));
    setOpen(false);
  };

  const display = displayDate(value);

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <button
        ref={triggerRef}
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center gap-2 text-left',
          !display && 'text-slate-500 dark:text-slate-400',
          display && 'text-slate-900 dark:text-white',
          error && 'input-error',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <CalendarTodayIcon className="shrink-0 text-primary-600 dark:text-primary-400" sx={{ fontSize: 18 }} />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {name && (
        <input type="hidden" name={name} value={value} required={isRequired} readOnly />
      )}
      {open &&
        panelCoords &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[300]"
            style={{ top: panelCoords.top, left: panelCoords.left, width: panelCoords.width }}
          >
            <PickerPopoverShell>
              <CalendarPopover
                embedded
                value={value}
                viewMonth={viewMonth}
                onViewMonthChange={setViewMonth}
                onSelect={pick}
                min={min}
                max={max}
              />
            </PickerPopoverShell>
          </div>,
          document.body,
        )}
      {error && (
        <p className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
